import { TAppointmentItem } from '../client-site/appointment.interface';
import { TCartItem } from '../client-site/user-choices.interface';
import { CouponInvalidReasonEnum } from './coupon.interface';

export enum ProductOrderStatusEnum {
  // Created, waiting for the online payment to be confirmed. Invisible to the admin.
  PendingPayment = 'pending-payment',
  // Paid — the business needs to prepare it.
  Received = 'received',
  // Prepared, waiting for the customer to pick it up.
  Ready = 'ready',
  PickedUp = 'picked-up',
  Cancelled = 'cancelled'
}

export interface IProductOrder {
  _id?: string;
  id?: string;
  bid: string;
  // Human-readable per-business sequence, shown to both sides at pickup.
  orderNumber: number;
  customerId: string;
  customerName: string;
  customerPhone: string;
  items: Array<TAppointmentItem>;
  /** Amount actually charged — the subtotal less any coupon discount. */
  totalAmount: number;
  /** Item subtotal before a coupon. Absent on orders bought without one. */
  subtotalAmount?: number;
  /** The coupon spent on this order, when one was. */
  couponDiscount?: {
    couponId: string;
    code: string;
    amount: number;
  };
  status: ProductOrderStatusEnum;
  // Pickup branch ('main' sentinel = primary location). Absent for single-location businesses.
  branchId?: string;
  customerNote?: string;
  paymentRequestUuid?: string;
  paidAt?: Date | string;
  /** Collect-card businesses: card saved at checkout, not charged yet (charged later from the order) */
  cardSavedAt?: Date | string;
  readyAt?: Date | string;
  pickedUpAt?: Date | string;
  cancelledAt?: Date | string;
  // When the "order ready" SMS/WhatsApp was sent to the customer.
  readyNotificationSentAt?: Date | string;
  source?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface ICreateProductOrderRequest {
  items: Array<TCartItem>;
  branchId?: string;
  customerNote?: string;
  source?: string;
  /** Promo code entered at checkout. Re-validated server-side before charging. */
  couponCode?: string;
}

export interface ICreateProductOrderResponse {
  orderId: string;
  orderNumber: number;
  paymentUuid: string;
  /** What the customer will be charged — already net of any coupon. */
  paymentAmount: number;
  /**
   * Set when the coupon sent along was rejected at order time. The order is still
   * created, at the undiscounted price, so the client has to say what happened.
   */
  couponError?: CouponInvalidReasonEnum;
}

/**
 * What a coupon would take off a products-only order, before the order exists.
 * The cart is priced server-side from the shop, so the discount can never be computed
 * against a total the browser made up.
 */
export interface IPreviewProductOrderCouponRequest {
  items: Array<TCartItem>;
  couponCode: string;
}

export interface IPreviewProductOrderCouponResponse {
  valid: boolean;
  reason?: CouponInvalidReasonEnum;
  code?: string;
  discountAmount: number;
  finalPrice: number;
}

// Public confirmation payload (order confirmation page — no auth, scoped to bid + order id).
export interface IProductOrderConfirmation {
  orderId: string;
  orderNumber: number;
  status: ProductOrderStatusEnum;
  items: Array<TAppointmentItem>;
  totalAmount: number;
  subtotalAmount?: number;
  couponDiscount?: {
    code: string;
    amount: number;
  };
  customerName: string;
  branchId?: string;
  branchName?: string;
  branchAddress?: string;
  pickupPolicy?: string;
  createdAt: Date | string;
  /** Placed on a saved card (collect-card business) — not charged yet */
  cardSavedOnly?: boolean;
}

// Admin-initiated sale (walk-in / phone customer) — creates an order + payment request.
export interface ICreateManualProductOrderRequest {
  customerId: string;
  items: Array<TCartItem>;
  branchId?: string;
  customerNote?: string;
}

export interface ICreateManualProductOrderResponse {
  orderId: string;
  orderNumber: number;
  paymentUuid: string;
  totalAmount: number;
}
